export interface MarketingConsent {
  /**
   * Marketing consent ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number.
   * @readonly
   */
  revision?: string | null;
  /** Marketing consent communication details. */
  details?: MarketingConsentDetails;
  /**
   * Marketing consent state.
   *
   * Default: `UNKNOWN_STATE`.
   */
  state?: MarketingConsentStateWithLiterals;
  /**
   * Date and time the marketing consent was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the marketing consent was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** Last confirmation activity of the marketing consent. */
  lastConfirmationActivity?: LastConfirmationActivity;
  /** Last revoke activity of the marketing consent. */
  lastRevokeActivity?: LastRevokeActivity;
  /** Additional fields. */
  extendedFields?: ExtendedFields;
}

export interface MarketingConsentDetails
  extends MarketingConsentDetailsIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
  /** The communication channel of the marketing consent. */
  type?: MarketingConsentDetailsIdentifierTypeWithLiterals;
}

/** @oneof */
export interface MarketingConsentDetailsIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
}

export enum MarketingConsentDetailsIdentifierType {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
}

/** @enumType */
export type MarketingConsentDetailsIdentifierTypeWithLiterals =
  | MarketingConsentDetailsIdentifierType
  | 'UNKNOWN_TYPE'
  | 'EMAIL'
  | 'PHONE';

export enum MarketingConsentState {
  /** State of the marketing consent is unknown. */
  UNKNOWN_STATE = 'UNKNOWN_STATE',
  /** The site visitor never confirmed to receive marketing consents. */
  NEVER_CONFIRMED = 'NEVER_CONFIRMED',
  /** The marketing consent has been removed, for example, when a site visitor unsubscribes from a newsletter. */
  REVOKED = 'REVOKED',
  /** The marketing consent is pending confirmation. Relevant only for `{"optInLevel": "DOUBLE_CONFIRMATION"}`. */
  PENDING = 'PENDING',
  /** The site visitor has confirmed their marketing consent. */
  CONFIRMED = 'CONFIRMED',
}

/** @enumType */
export type MarketingConsentStateWithLiterals =
  | MarketingConsentState
  | 'UNKNOWN_STATE'
  | 'NEVER_CONFIRMED'
  | 'REVOKED'
  | 'PENDING'
  | 'CONFIRMED';

export interface LastConfirmationActivity {
  /** Source of the given consent (how the site visitor signed up). */
  source?: SourceWithLiterals;
  /**
   * Consent description.
   * @minLength 1
   * @maxLength 200
   */
  description?: string | null;
  /** Date and time the consent was updated. */
  updatedDate?: Date | null;
  /** Consent opt in level, either single or double confirmation. */
  optInLevel?: OptInLevelWithLiterals;
}

export enum Source {
  IN_PERSON = 'IN_PERSON',
  FORM = 'FORM',
  LINK_CONFIRMATION = 'LINK_CONFIRMATION',
  EMAIL_SERVICE = 'EMAIL_SERVICE',
  WIX_USERS = 'WIX_USERS',
  OTHER = 'OTHER',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'IN_PERSON'
  | 'FORM'
  | 'LINK_CONFIRMATION'
  | 'EMAIL_SERVICE'
  | 'WIX_USERS'
  | 'OTHER';

export enum OptInLevel {
  UNKNOWN_OPT_IN_LEVEL = 'UNKNOWN_OPT_IN_LEVEL',
  SINGLE_CONFIRMATION = 'SINGLE_CONFIRMATION',
  DOUBLE_CONFIRMATION = 'DOUBLE_CONFIRMATION',
}

/** @enumType */
export type OptInLevelWithLiterals =
  | OptInLevel
  | 'UNKNOWN_OPT_IN_LEVEL'
  | 'SINGLE_CONFIRMATION'
  | 'DOUBLE_CONFIRMATION';

export interface LastRevokeActivity {
  /** Source of the given revoke (how the visitor signed up). */
  source?: LastRevokeActivitySourceWithLiterals;
  /**
   * Details about the revoke, if relevant.
   * @minLength 1
   * @maxLength 200
   */
  description?: string | null;
  /** Date and time the consent was updated. */
  updatedDate?: Date | null;
}

export enum LastRevokeActivitySource {
  IN_PERSON = 'IN_PERSON',
  FORM = 'FORM',
  REVOKE_LINK = 'REVOKE_LINK',
  WIX_USERS = 'WIX_USERS',
  OTHER = 'OTHER',
}

/** @enumType */
export type LastRevokeActivitySourceWithLiterals =
  | LastRevokeActivitySource
  | 'IN_PERSON'
  | 'FORM'
  | 'REVOKE_LINK'
  | 'WIX_USERS'
  | 'OTHER';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GetMarketingConsentRequest {
  /**
   * Marketing consent ID.
   * @format GUID
   */
  marketingConsentId: string;
}

export interface GetMarketingConsentResponse {
  /** The requested marketing consent. */
  marketingConsent?: MarketingConsent;
}

export interface GetMarketingConsentByIdentifierRequest
  extends GetMarketingConsentByIdentifierRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
  /** The communication channel of the marketing consent. */
  type: MarketingConsentDetailsIdentifierTypeWithLiterals;
  /**
   * Language of the page
   * @format LANGUAGE
   */
  linkLanguage?: string | null;
}

/** @oneof */
export interface GetMarketingConsentByIdentifierRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
}

export interface GetMarketingConsentByIdentifierResponse {
  /** The requested marketing consent. */
  marketingConsent?: MarketingConsent;
  /** Details about whether the subject of the marketing consent is eligible to receive marketing messages. */
  communicationEligibility?: CommunicationEligibility;
}

export interface CommunicationEligibility {
  /** Whether the recipient of the marketing consent is eligible to receive marketing messages. For example, if a visitor cancels their marketing consent, `CommunicationEligibility.granted` is `false`. Note that this only serves as a signal for your app to decide whether or not it should send marketing messages to the recipient's email address or phone number. */
  granted?: boolean;
  /**
   * Cancellation link.
   * @format WEB_URL
   */
  revokeConfirmationLink?: string | null;
  /**
   * The reason the recipient of the marketing consent isn't eligible to receive marketing messages, for example, if the visitor unsubscribed from a newsletter.
   * @maxLength 300
   */
  reason?: string | null;
}

export interface GetConsistentMarketingConsentByIdentifierRequest
  extends GetConsistentMarketingConsentByIdentifierRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
  /** The communication channel of the marketing consent. */
  type?: MarketingConsentDetailsIdentifierTypeWithLiterals;
  /**
   * Language of the page
   * @format LANGUAGE
   */
  linkLanguage?: string | null;
}

/** @oneof */
export interface GetConsistentMarketingConsentByIdentifierRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
}

export interface GetConsistentMarketingConsentByIdentifierResponse {
  /** The requested marketing consent. */
  marketingConsent?: MarketingConsent;
  /** Details about whether the recipient of the marketing consent is eligible to receive marketing messages. */
  communicationEligibility?: CommunicationEligibility;
}

export interface CreateMarketingConsentRequest {
  /** Marketing consent to create. */
  marketingConsent?: MarketingConsent;
}

export interface CreateMarketingConsentResponse {
  /** Newly created marketing consent. */
  marketingConsent?: MarketingConsent;
  /** Marketing consent confirmation link. */
  link?: Link;
}

export interface Link {
  /** Link type. */
  type?: LinkTypeWithLiterals;
  /**
   * The link's URL.
   * @format WEB_URL
   */
  url?: string;
}

export enum LinkType {
  UNKNOWN_LINK_TYPE = 'UNKNOWN_LINK_TYPE',
  CONFIRMATION = 'CONFIRMATION',
  REVOKE_CONFIRMATION = 'REVOKE_CONFIRMATION',
}

/** @enumType */
export type LinkTypeWithLiterals =
  | LinkType
  | 'UNKNOWN_LINK_TYPE'
  | 'CONFIRMATION'
  | 'REVOKE_CONFIRMATION';

export interface UpdateMarketingConsentRequest {
  /** Marketing consent to update. */
  marketingConsent?: MarketingConsent;
  /** Explicit list of fields to update */
  mask: string[];
}

export interface UpdateMarketingConsentResponse {
  /** Updated marketing consent. */
  marketingConsent?: MarketingConsent;
  /** Marketing consent confirmation or cancelation link. */
  link?: Link;
}

export interface DeleteMarketingConsentRequest {
  /**
   * ID of the marketing consent to delete.
   * @format GUID
   */
  marketingConsentId: string;
}

export interface DeleteMarketingConsentResponse {}

export interface RemoveMarketingConsentRequest {
  /** Marketing consent communication details. */
  details: MarketingConsentDetails;
  /** Information about the last revoke. */
  lastRevokeActivity?: LastRevokeActivity;
}

export interface RemoveMarketingConsentResponse {
  /** The canceled marketing consent. */
  marketingConsent?: MarketingConsent;
}

export interface CreateMarketingConsentByTokenRequest {
  /**
   * Encrypted token with essential information
   * @maxLength 5000
   */
  token?: string;
}

export interface CreateMarketingConsentByTokenResponse {
  /** The subscribed MarketingConsent */
  marketingConsent?: MarketingConsent;
}

export interface RemoveMarketingConsentByTokenRequest {
  /**
   * Encrypted token with essential information
   * @maxLength 5000
   */
  token?: string;
}

export interface RemoveMarketingConsentByTokenResponse {
  /** The subscribed MarketingConsent */
  marketingConsent?: MarketingConsent;
}

export interface QueryMarketingConsentRequest {
  /** Query options. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryMarketingConsentResponse {
  /** List of marketing consents. */
  marketingConsent?: MarketingConsent[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
  /**
   * Total number of items matching the filter.
   * Available only on the first page of *Search* results, not included in *Query* or *List* results.
   * If the Search results span multiple pages, the value of `total` will exceed the number of items returned on the first page.
   * @internal
   */
  total?: number | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpsertMarketingConsentRequest {
  /** Marketing consent to create or update. */
  marketingConsent?: MarketingConsent;
}

export interface UpsertMarketingConsentResponse {
  /** Newly created or updated marketing consent. */
  marketingConsent?: MarketingConsent;
  /** Marketing consent confirmation or cancelation link. */
  link?: Link;
}

export interface BulkUpsertMarketingConsentRequest {
  /**
   * List of marketing consent information to update or create.
   * @minSize 1
   * @maxSize 500
   */
  info?: MarketingConsent[];
}

export interface BulkUpsertMarketingConsentResponse {
  /** List of created or updated marketing consents. */
  results?: BulkUpsertMarketingConsentResult[];
  /** Numbers of successful and failed actions. */
  metadata?: Metadata;
}

export interface BulkUpsertMarketingConsentResult {
  /** Position of the newly created or updated marketing consent in the array. */
  originalIndex?: number;
  /** Newly created or updated marketing consent. */
  marketingConsent?: MarketingConsent;
  /**
   * Information about the error. Only returns if the action fails.
   * @maxLength 500
   */
  error?: string | null;
  /** Marketing consent confirmation or cancelation link. */
  link?: Link;
}

export interface Metadata {
  /** Number of successful actions. */
  totalSuccess?: number;
  /** Number of failed actions. */
  totalFailure?: number;
}

export interface GenerateLinkRequest
  extends GenerateLinkRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
  /** The communication channel of the marketing consent. */
  type?: MarketingConsentDetailsIdentifierTypeWithLiterals;
  linkType?: LinkTypeWithLiterals;
  /**
   * Language of the page
   * @format LANGUAGE
   */
  language?: string | null;
  /** Arbitrary parameters for closing-the-loop. */
  metadata?: Record<string, string>;
}

/** @oneof */
export interface GenerateLinkRequestIdentifierOneOf {
  /**
   * Email address used for the marketing consent.
   * @format EMAIL
   */
  email?: string;
  /**
   * Phone number in [E.164](https://en.wikipedia.org/wiki/E.164) format used for the SMS marketing consent.
   * @format PHONE
   */
  phone?: string;
}

export interface GenerateLinkResponse {
  /** confirmation or revoke link */
  link?: Link;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
